import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { checkForDeploymentNow } from '@/utils/deployment-detector';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isDeploymentError: boolean;
  retryCount: number;
}

export class DeploymentErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;
  
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      isDeploymentError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isDeploymentError = DeploymentErrorBoundary.isDeploymentRelatedError(error);
    
    return { 
      hasError: true, 
      error,
      isDeploymentError
    };
  }

  static isDeploymentRelatedError(error: Error): boolean {
    if (!error) return false;
    
    const message = error.message?.toLowerCase() || '';
    const stack = error.stack?.toLowerCase() || '';
    
    // Check for various deployment-related error patterns
    const deploymentPatterns = [
      'failed to fetch dynamically imported module',
      'loading chunk',
      'chunkerror',
      'networkerror',
      'script error',
      'failed to fetch',
      'module not found',
      'loading css chunk',
      'unexpected token',
      'syntax error',
      'cannot resolve module',
      'import error'
    ];
    
    return deploymentPatterns.some(pattern => 
      message.includes(pattern) || stack.includes(pattern)
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('DeploymentErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // If it's a deployment error, trigger deployment check
    if (this.state.isDeploymentError) {
      console.log('Deployment-related error detected, checking for new deployment');
      setTimeout(() => {
        checkForDeploymentNow();
      }, 1000);
    }
  }

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    // If we've retried multiple times and it's a deployment error, force reload
    if (newRetryCount > 2 && this.state.isDeploymentError) {
      console.log('Multiple retries for deployment error, forcing page reload');
      window.location.reload();
      return;
    }
    
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: newRetryCount
    });
  };

  handleForceReload = () => {
    // Clear any pending timeouts
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    
    console.log('Force reloading page due to deployment error');
    window.location.reload();
  };

  handleAutoRetry = () => {
    if (this.state.isDeploymentError && this.state.retryCount < 3) {
      console.log(`Auto-retrying deployment error (attempt ${this.state.retryCount + 1})`);
      
      this.retryTimeoutId = window.setTimeout(() => {
        this.handleRetry();
      }, 2000 * (this.state.retryCount + 1)); // Exponential backoff
    }
  };

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // Auto-retry for deployment errors
    if (!prevState.hasError && this.state.hasError && this.state.isDeploymentError) {
      this.handleAutoRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { isDeploymentError, retryCount } = this.state;

      // Deployment-specific error UI
      if (isDeploymentError) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
            <Card className="w-full max-w-lg">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  {navigator.onLine ? (
                    <Wifi className="h-16 w-16 text-blue-500" />
                  ) : (
                    <WifiOff className="h-16 w-16 text-red-500" />
                  )}
                </div>
                <CardTitle className="text-2xl text-blue-600">
                  Učitavanje nove verzije...
                </CardTitle>
                <CardDescription>
                  Aplikacija se ažurira na najnoviju verziju.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <Alert>
                  <RefreshCw className="h-4 w-4" />
                  <AlertDescription>
                    Aplikacija trenutno ima problema sa povezivanjem. Ovo može biti zbog održavanja servera ili problema sa mrežom. Molimo pokušajte za nekoliko trenutaka.
                  </AlertDescription>
                </Alert>

                <div className="text-sm text-gray-600 space-y-2">
                  <p><strong>Šta možete probati:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Sačekajte trenutak da se aplikacija automatski ažurira</li>
                    <li>Proverite internetsku konekciju</li>
                    <li>Osvežite stranicu ako se problem nastavi</li>
                  </ul>
                </div>

                <div className="flex flex-col gap-2">
                  {retryCount < 3 ? (
                    <Button onClick={this.handleRetry} className="w-full">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Pokušaj ponovo ({retryCount + 1}/3)
                    </Button>
                  ) : (
                    <Button onClick={this.handleForceReload} className="w-full">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Osveži stranicu
                    </Button>
                  )}
                </div>

                {retryCount > 0 && (
                  <div className="text-xs text-center text-gray-500">
                    Pokušaj {retryCount} od 3
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      }

      // Generic error UI for non-deployment errors
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-16 w-16 text-red-500" />
              </div>
              <CardTitle className="text-2xl text-red-600">Dogodila se greška</CardTitle>
              <CardDescription>
                Neočekivana greška je nastala prilikom učitavanja ove stranice.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="font-mono text-sm">
                  {this.state.error?.message || 'Nepoznata greška'}
                </AlertDescription>
              </Alert>

              <div className="flex flex-col gap-2">
                <Button onClick={this.handleRetry} className="w-full">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Pokušaj ponovo
                </Button>
                
                <Button variant="outline" onClick={this.handleForceReload}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Osveži stranicu
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
} 